/* eslint-disable @typescript-eslint/no-unused-vars */
import { AxiosResponse } from "axios";
import { GetSingleOrderDetailsResponse } from "../../@types/DRO/PurchaseOrderLinesResponse";
import {
    CreatePurchaseOrderApiResponse,
    GeneralResponseForAllApi,
    GetPurchaseOrderListResponse,
    PurchaseOrderHistoryResponse,
} from "../../@types/DRO/PurchaseOrderResponse";
import { PurchaseOrderBody } from "../../@types/DTO/CreatePurchaseOrderBody"; // Ensure this import is added
import { erpAxios, getAuthToken } from "../axiosInstances";

export class ProductOrderApi {
  public static api = new ProductOrderApi();
  private instance = erpAxios;

  getToken() {
    return getAuthToken();
  }

  async getPurchaseOrderList(
    page?: number,
    pageSize?: number,
    search?: string,
    startDate?: string,
    endDate?: string,
    myApprovalsOnly?: boolean,
    status?: string
  ): Promise<GetPurchaseOrderListResponse> {
    try {
      const response: AxiosResponse<GetPurchaseOrderListResponse> =
        await this.instance.get("/erp/api/v1/purchase-order/all", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            pageSize,
            search,
            startDate,
            endDate,
            myApprovalsOnly,
            status,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching predictions all data", error);
      throw error;
    }
  }

  async createPurchaseOrder(
    orderBody: PurchaseOrderBody
  ): Promise<CreatePurchaseOrderApiResponse> {
    try {
      const response: AxiosResponse<CreatePurchaseOrderApiResponse> =
        await this.instance.post(
          "/erp/api/v1/purchase-order/create",
          orderBody,
          {
            headers: {
              Authorization: `Bearer ${this.getToken()}`,
            },
          }
        );
      return response.data;
    } catch (error) {
      console.error("Error creating purchase order", error);
      throw error;
    }
  }

  async updatePurchaseOrder(
    id: string,
    orderBody: PurchaseOrderBody
  ): Promise<CreatePurchaseOrderApiResponse> {
    try {
      const response: AxiosResponse<CreatePurchaseOrderApiResponse> =
        await this.instance.put(
          `/erp/api/v1/purchase-order/update?id=${id}`,
          orderBody,
          {
            headers: {
              Authorization: `Bearer ${this.getToken()}`,
            },
          }
        );
      return response.data;
    } catch (error) {
      console.error("Error updating purchase order", error);
      throw error;
    }
  }

  async getSingleProductOrderDetails(
    id: number | null
  ): Promise<GetSingleOrderDetailsResponse> {
    try {
      const response: AxiosResponse<GetSingleOrderDetailsResponse> =
        await this.instance.get(`/erp/api/v1/purchase-order/${id}`, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching predictions all data", error);
      throw error;
    }
  }

  async getSingleProductOrderDetailsByCriteria(
    partId: number,
    warehouseId: number,
    vendorId: number
  ): Promise<GetSingleOrderDetailsResponse> {
    try {
      const response: AxiosResponse<GetSingleOrderDetailsResponse> =
        await this.instance.get(`/erp/api/v1/purchase-order/by-criteria`, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            partId,
            warehouseId,
            vendorId,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching product order details", error);
      throw error;
    }
  }
  async cancelPurchaseOrder(id: number): Promise<GeneralResponseForAllApi> {
    try {
      const response: AxiosResponse<GeneralResponseForAllApi> =
        await this.instance.put(
          `/erp/api/v1/purchase-order/cancel?id=${id}`,
          null,
          {
            headers: {
              Authorization: `Bearer ${this.getToken()}`,
            },
          }
        );
      return response.data;
    } catch (error) {
      console.error("Error cancelling purchase order", error);
      throw error;
    }
  }

  async closePurchaseOrder(id: number): Promise<GeneralResponseForAllApi> {
    try {
      const response: AxiosResponse<GeneralResponseForAllApi> =
        await this.instance.put(
          `/erp/api/v1/purchase-order/close?id=${id}`,
          null,
          {
            headers: {
              Authorization: `Bearer ${this.getToken()}`,
            },
          }
        );
      return response.data;
    } catch (error) {
      console.error("Error closing purchase order", error);
      throw error;
    }
  }

  async getPurchaseOrderHistory(id: number): Promise<PurchaseOrderHistoryResponse> {
    try {
      const response: AxiosResponse<PurchaseOrderHistoryResponse> =
        await this.instance.get(`/erp/api/v1/purchase-order/history`, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            id,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching purchase order history", error);
      throw error;
    }
  }

  async editPurchaseOrder(
    purchaseOrderId: number,
    orderLines: Array<{
      partId: number;
      quantityOrdered: number;
      price: number;
      tax: number;
    }>
  ): Promise<{
    id: number;
    status: {
      name: string;
      displayName: string;
    };
    purchaseOrderType: "OPEN";
    vendorDetails: {
      vendorId: number;
      vendorName: string;
      vendorAddressLineOne: string;
      vendorAddressLineTwo: string;
    };
    deliveryWareHouseDetails: {
      deliveryWarehouseId: number;
      deliveryWarehouseName: string;
      deliveryWarehouseCode: string;
    };
    gstTreatment: string;
    confirmationDate: string;
    expectedArrival: string;
    updatedBy: number;
    notes: string;
    orderDate: string;
    amount: number;
    approvals: Array<{
      id: number;
      name: string;
      status: string;
    }>;
    purchaseOrderLines: Array<{
      purchaseOrderId: number;
      partId: number;
      partName: string;
      fleetType: string;
      referenceCode: string;
      tax: number;
      quantityOrdered: number;
      demandLeft: number;
      quantityReceived: number;
      unitPrice: number;
      price: number;
      hsn: string;
    }>;
    untaxedAmount: number;
    igstAmount: number;
    totalAmount: number;
    amountInWords: string;
  }> {
    try {
      const response = await this.instance.put(
        `/erp/api/v1/purchase-order/edit?purchaseOrderId=${purchaseOrderId}`,
        orderLines,
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error editing purchase order", error);
      throw error;
    }
  }
}
